import { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';

import { ChevronDownIcon, ChevronUpIcon, CloseIcon } from '../Icons';

import Styles from './index.module.scss';
import { useDropdownHandlers } from './useDropdownHandlers';

type Value = string;

interface Props {
	value?: Value[];
	onChange?: (value: Value[]) => void;
	children?: React.ReactNode;
}

const SelectContext = createContext<Props | null>(null);

export const Select = ({ value, onChange, children }: Props) => {
	const [open, setOpen] = useState(false);
	const { containerRef, popupRef } = useDropdownHandlers(setOpen, open);

	return (
		<SelectContext.Provider value={{ value, onChange }}>
			<button
				ref={containerRef}
				className={Styles.container}
				type="button"
				onClick={() => {
					requestAnimationFrame(() => {
						setOpen(!open);
					});
				}}
			>
				<p>Select Organizations</p>
				{open ? <ChevronUpIcon /> : <ChevronDownIcon />}
			</button>

			{open &&
				createPortal(
					<div ref={popupRef} className={Styles.popup}>
						<div className={Styles.content}>
							<div className={Styles.list}>{children}</div>
						</div>
					</div>,
					document.body
				)}
		</SelectContext.Provider>
	);
};

interface SelectOptionProps {
	value: Value;
	children: React.ReactNode;
}

const Option = ({ value, children }: SelectOptionProps) => {
	const context = useContext(SelectContext);

	if (!context) {
		throw new Error('Select.Option must be used within a Select');
	}
	const { value: selectedValues, onChange } = context;
	const isSelected = selectedValues?.includes(value);

	const handleClick = () => {
		if (isSelected) {
			onChange?.(selectedValues?.filter((v) => v !== value) ?? []);
		} else {
			onChange?.([...(selectedValues ?? []), value]);
		}
	};

	return (
		<button
			type="button"
			className={isSelected ? Styles.selected : Styles.item}
			onClick={handleClick}
		>
			<p>{children}</p>
			<div className={Styles.marker}>{isSelected ? <CloseIcon /> : '+'}</div>
		</button>
	);
};

Select.Option = Option;
